import { z } from 'zod';
import { isGoogleFontFamily } from './google-fonts.manifest';

// Curated, OFL-licensed, PDF-embeddable typefaces (IMM-223). Split into Serif
// and Sans families, both selectable for every role; free text is rejected so an
// unverified face can never reach the Exposé PDF. Display names (not CSS family
// strings) — the client maps a name to its loaded face.
export const CURATED_FONTS = [
  // Serif
  'Fraunces',
  'Cormorant Garamond',
  'Playfair Display',
  'EB Garamond',
  'Spectral',
  'Lora',
  // Sans
  'Inter',
  'Source Sans 3',
  'Work Sans',
  'Libre Franklin',
  'IBM Plex Sans',
] as const;

export const FontSchema = z.enum(CURATED_FONTS);

export type Font = z.infer<typeof FontSchema>;

// Where a role's font comes from (IMM-391). `uploaded` (own-font upload) is
// descoped from IMM-391 — the model admits only curated quick-picks and the
// Google Fonts library.
export const FontSourceSchema = z.enum(['curated', 'google']);

export type FontSource = z.infer<typeof FontSourceSchema>;

// Bounds the free-text `google` family name; `curated` families are enum-checked
// against CURATED_FONTS, not by length.
const ROLE_FONT_FAMILY_MAX_LENGTH = 100;

// A role's font (IMM-391.1): a source-qualified reference that replaces the former
// bare curated-enum value. A `curated` family must be one of the eleven; a `google`
// family must be one of the vendored Google Fonts manifest (IMM-391.2.1) so the
// picker can never offer, nor a caller submit, a family the renderer could not
// later self-host. Legacy stored curated names map onto `{ source: 'curated',
// family }` on read, so no data migration is needed.
export const RoleFontSchema = z
  .object({
    source: FontSourceSchema,
    family: z.string().trim().min(1).max(ROLE_FONT_FAMILY_MAX_LENGTH),
  })
  .refine((value) => value.source !== 'curated' || (CURATED_FONTS as ReadonlyArray<string>).includes(value.family), {
    path: ['family'],
    message: 'Unknown curated font family',
  })
  .refine((value) => value.source !== 'google' || isGoogleFontFamily(value.family), {
    path: ['family'],
    message: 'Unknown Google Fonts family',
  });

export type RoleFont = z.infer<typeof RoleFontSchema>;

// Letter-casing for the label role only (UPPER_CASE union per the domain-constant
// convention; German display labels live in `@immo/i18n`).
export const LabelCasingSchema = z.enum(['NORMAL', 'UPPERCASE', 'SMALL_CAPS']);

export type LabelCasing = z.infer<typeof LabelCasingSchema>;

export const FontRoleSchema = z.enum(['heading', 'body', 'label']);

export type FontRole = z.infer<typeof FontRoleSchema>;

// A non-blocking warning (IMM-391.2.2.2) that a selected Google font is missing
// some of the required German glyphs (ä/ö/ü/ß, €, ²). Returned by the branding
// update only; the selection still succeeds. Coverage is a warning, never a block.
export const GlyphCoverageWarningSchema = z.object({
  role: FontRoleSchema,
  family: z.string(),
});

export type GlyphCoverageWarning = z.infer<typeof GlyphCoverageWarningSchema>;

// `#RGB` or `#RRGGBB`. The German validation hint is rendered client-side from
// the field error — the schema only decides validity.
const HexColorSchema = z.string().regex(/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/);

// Letter-spacing for the label role only: a CSS `em` or `px` length (e.g.
// "0.08em"). Negative tracking is permitted (tight caps).
const LabelTrackingSchema = z.string().regex(/^-?\d*\.?\d+(em|px)$/);

const emptyToNull = (value: unknown): unknown => (typeof value === 'string' && value.trim() === '' ? null : value);

// Optional field: an empty/whitespace submission means "not set" (→ null);
// `.default(null)` lets the key be omitted entirely.
const optional = <T extends z.ZodTypeAny>(schema: T) => z.preprocess(emptyToNull, schema.nullable()).default(null);

// Outward visual identity (IMM-223.1): house + document colours and role
// typography. Logo variants and favicon (IMM-223.2) and the IMM-220 consumption
// port (IMM-223.3) are intentionally absent here. `brandColour` is the primary
// house colour (the existing `workspace_branding.brand_colour` column).
export const WorkspaceBrandingResponseSchema = z.object({
  workspaceId: z.uuid(),
  // House colours.
  brandColour: z.string().nullable(),
  secondaryColor: z.string().nullable(),
  accentColor: z.string().nullable(),
  // Document colours.
  paperColor: z.string().nullable(),
  inkColor: z.string().nullable(),
  mutedColor: z.string().nullable(),
  // Role typography (IMM-391.1): a source-qualified reference per role.
  headingFont: RoleFontSchema.nullable(),
  bodyFont: RoleFontSchema.nullable(),
  labelFont: RoleFontSchema.nullable(),
  labelCasing: LabelCasingSchema.nullable(),
  labelTracking: z.string().nullable(),
  // Non-blocking glyph-coverage warnings for newly self-hosted Google fonts
  // (IMM-391.2.2.2). Populated by the update endpoint; empty on read.
  glyphWarnings: z.array(GlyphCoverageWarningSchema).default([]),
  // Logo variants + favicon (IMM-223.2): media file ids (IMM-280). Null when
  // unset. The client resolves a display URL per id via the branding logo-read
  // endpoint (same handle the Exposé, IMM-220, consumes).
  logoPrimaryFileId: z.uuid().nullable(),
  logoLightFileId: z.uuid().nullable(),
  logoDarkFileId: z.uuid().nullable(),
  faviconFileId: z.uuid().nullable(),
  updatedAt: z.iso.datetime().nullable(),
});

export type WorkspaceBrandingResponse = z.infer<typeof WorkspaceBrandingResponseSchema>;

export const UpdateWorkspaceBrandingBodySchema = z
  .object({
    brandColour: optional(HexColorSchema),
    secondaryColor: optional(HexColorSchema),
    accentColor: optional(HexColorSchema),
    paperColor: optional(HexColorSchema),
    inkColor: optional(HexColorSchema),
    mutedColor: optional(HexColorSchema),
    headingFont: optional(RoleFontSchema),
    bodyFont: optional(RoleFontSchema),
    labelFont: optional(RoleFontSchema),
    labelCasing: optional(LabelCasingSchema),
    labelTracking: optional(LabelTrackingSchema),
    // A confirmed media file id to set, null to clear, or omitted to leave the
    // variant unchanged (so a colour/typography save never disturbs the logos).
    // The server re-validates a new id references a ready file in the workspace
    // and deletes the previously-referenced media on replace/clear.
    logoPrimaryFileId: z.uuid().nullable().optional(),
    logoLightFileId: z.uuid().nullable().optional(),
    logoDarkFileId: z.uuid().nullable().optional(),
    faviconFileId: z.uuid().nullable().optional(),
  })
  .strict();

export type UpdateWorkspaceBrandingBody = z.infer<typeof UpdateWorkspaceBrandingBodySchema>;

export const WorkspaceBrandingPathParamsSchema = z.object({ workspaceId: z.uuid() });

// Logo / favicon upload policy (IMM-223.2). Consumer-owned constraints the
// branding logo-upload endpoint passes to the media module (IMM-280), which
// holds no policy of its own. SVG is deliberately excluded for V1 (no
// sanitiser; parent Resolved Decision 2). Favicon additionally accepts ICO.
export const BRANDING_LOGO_ALLOWED_CONTENT_TYPES = ['image/png', 'image/jpeg'] as const;
export const BRANDING_FAVICON_ALLOWED_CONTENT_TYPES = [
  'image/png',
  'image/vnd.microsoft.icon',
  'image/x-icon',
] as const;
export const BRANDING_LOGO_MAX_SIZE_BYTES = 5 * 1024 * 1024;
export const BRANDING_LOGO_DESCRIPTION = 'workspace-branding-logo';

export const BrandingLogoKindSchema = z.enum(['logo', 'favicon']);

export type BrandingLogoKind = z.infer<typeof BrandingLogoKindSchema>;

// Upload wrapper over the generic media flow, gated by branding-update instead
// of organization:media:file-write. The server fixes the allow-list (by `kind`)
// and size cap; the caller supplies only the declared type, size, and filename.
export const CreateBrandingLogoUploadPathParamsSchema = z.object({ workspaceId: z.uuid() });

export const CreateBrandingLogoUploadBodySchema = z
  .object({
    kind: BrandingLogoKindSchema,
    contentType: z.string().min(1).max(255),
    sizeBytes: z.number().int().positive(),
    originalFilename: z.string().trim().min(1).max(255).nullish(),
  })
  .strict();

export type CreateBrandingLogoUploadBody = z.infer<typeof CreateBrandingLogoUploadBodySchema>;

export const ConfirmBrandingLogoUploadPathParamsSchema = z.object({ workspaceId: z.uuid(), fileId: z.uuid() });

export const ReadBrandingLogoPathParamsSchema = z.object({ workspaceId: z.uuid(), fileId: z.uuid() });
